import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const TYPE_META = {
  agent_start: { color: "text-zinc-400", label: "Agent start" },
  browser_launch: { color: "text-amber-300", label: "Browser launch" },
  browser_install: { color: "text-amber-400", label: "Browser install" },
  browser_uninstall: { color: "text-amber-400", label: "Browser uninstall" },
  software_install: { color: "text-emerald-300", label: "Software install" },
  software_uninstall: { color: "text-emerald-400", label: "Software uninstall" },
  file_delete: { color: "text-red-300", label: "File deleted" },
  usb_mount: { color: "text-blue-300", label: "USB plugged" },
  usb_unmount: { color: "text-blue-400", label: "USB removed" },
};

function describe(e) {
  const d = e.detail || {};
  switch (e.type) {
    case "browser_launch": return `${d.name || "?"} launched (pid ${d.pid})`;
    case "browser_install":
    case "software_install": return `${d.name}${d.version ? ` v${d.version}` : ""}${d.publisher ? ` — ${d.publisher}` : ""}`;
    case "browser_uninstall":
    case "software_uninstall": return `Removed: ${d.name}`;
    case "file_delete": return d.path || "(unknown file)";
    case "usb_mount":
    case "usb_unmount": return `Drive ${d.drive}`;
    case "agent_start": return `${d.hostname || ""} — agent v${d.version || "?"}`;
    default: return JSON.stringify(d);
  }
}

export default function EndpointEvents() {
  const [items, setItems] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (typeFilter) params.type_filter = typeFilter;
      const [{ data: ev }, { data: ep }] = await Promise.all([
        api.get("/admin/endpoint-events", { params }),
        api.get("/admin/endpoints"),
      ]);
      setItems(ev);
      setEndpoints(ep);
    } finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((i) =>
    !search ||
    (i.user_email || "").toLowerCase().includes(search.toLowerCase()) ||
    JSON.stringify(i.detail || {}).toLowerCase().includes(search.toLowerCase())
  );

  const typeButtons = ["", "browser_launch", "software_install", "software_uninstall", "file_delete", "usb_mount", "browser_install"];

  return (
    <div className="p-10 max-w-6xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-widest text-emerald-400/80">ENDPOINT MONITOR</div>
          <h1 className="text-2xl font-semibold mt-1">Workstation activity</h1>
          <p className="text-xs text-zinc-500 mt-1">{endpoints.length} workstation(s) registered. Events stream in from the SND Endpoint Agent.</p>
        </div>
        <input
          data-testid="endpoint-search"
          placeholder="Search user / file / app…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm w-72"
        />
      </div>

      {/* Endpoints summary */}
      {endpoints.length > 0 && (
        <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] p-4 mb-6">
          <div className="text-xs tracking-widest text-zinc-500 uppercase mb-3">Registered workstations</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {endpoints.map((e) => (
              <div key={`${e.user_id}-${e.machine_id}`} className="flex justify-between items-center text-sm p-3 rounded-md bg-zinc-900/50 border border-zinc-800">
                <div>
                  <div className="text-zinc-200">{e.user_email}</div>
                  <div className="text-xs text-zinc-500">{e.hostname || "(no hostname)"} · {e.os || ""}</div>
                </div>
                <div className="text-right text-[11px] text-zinc-500">
                  <div>last seen</div>
                  <div className="text-emerald-300">{e.last_seen ? new Date(e.last_seen).toLocaleString() : "—"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Type filter */}
      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        {typeButtons.map((t) => (
          <button key={t || "all"} onClick={() => setTypeFilter(t)}
            data-testid={`type-filter-${t || "all"}`}
            className={`px-3 py-1.5 rounded-md border ${typeFilter === t ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
            {t ? (TYPE_META[t]?.label || t) : "All"}
          </button>
        ))}
      </div>

      {/* Event feed */}
      <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] divide-y divide-zinc-900">
        {loading && <div className="p-6 text-sm text-zinc-500">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="p-6 text-sm text-zinc-500">No events yet.</div>}
        {filtered.map((e) => {
          const meta = TYPE_META[e.type] || { color: "text-zinc-300", label: e.type };
          return (
            <div key={e.id} className="p-4 flex items-center justify-between text-sm gap-4">
              <div className="min-w-0 flex-1">
                <div className={`text-xs uppercase tracking-wider ${meta.color}`}>{meta.label}</div>
                <div className="text-zinc-200 mt-1 truncate" title={describe(e)}>{describe(e)}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-emerald-300">{e.user_email}</div>
                <div className="text-[11px] text-zinc-500">{e.hostname || e.machine_id?.slice(0, 8)}</div>
                <div className="text-[11px] text-zinc-600">{new Date(e.ts).toLocaleString()}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
